import * as React from "react";
import { CompetitionRequestApi, CompetitionRequestExpoDaysDto, ExpoDto } from "../../../../api";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ERoutes } from "../../../../ERoutes";
import { AddButton, DeleteButton, FGSRSHDateMaskInput, SmallFormGenerator } from "../../../../components";
import * as Yup from "yup";
import {
  FGCheckboxInput,
  FGDateMaskInput,
  FGMaskInput,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  showSuccess,
  useFGFormik,
} from "nsitools-react";
import { formatDate } from "../../../../utils";
import { ETLCodes } from "../../../../locales";
import { useTranslation } from "react-i18next";
import { useReferential } from "../../../../hooks/useReferential";
import { FormikProps } from "formik";
import { Button, Classes, Colors, Dialog } from "@blueprintjs/core";
import { TimePicker } from "@blueprintjs/datetime";
export interface IExpoTabProps {
  competitionRequestId: number;
  editMode: boolean;
}

export const ExpoTab: React.FunctionComponent<IExpoTabProps> = ({ competitionRequestId, editMode }) => {
  const competitionRequestApi = useApiService(CompetitionRequestApi);
  const { t } = useTl();

  const [selectedDayDate, setselectedDayDate] = React.useState<number | null>(null);
  const [subActivities, subActivitiesLoading] = useReferential(
    (a) => a.getCompetitionRequestSubActivities(competitionRequestId),
    true,
    []
  );
  const { data, loading, saveItem, refresh, saving } = useCrudApi<CompetitionRequestExpoDaysDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = competitionRequestId) =>
          id > 0 ? competitionRequestApi.getCompetitionRequestExpos(id) : formik?.values,
        saveApiFn: async (data) => {
          var res = await competitionRequestApi.saveCompetitionRequestExpos(data);
          return res;
        },
        onSavedRoute: (d) => `${ERoutes.competitionRequest}/${d.competitionRequestId}/expos/edit`,
        deleteApiFn: (d) => {},
        onDeletedRoute: () => "",
      }),
      []
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        competition: Yup.object().shape({
          competitionDays: Yup.array().of(
            Yup.object().shape({
              expos: Yup.array().of(
                Yup.object().shape({
                  subActivityId: Yup.number().nullable().required(t(ETLCodes.Required)),
                })
              ),
            })
          ),
        }),
      }),
    [t]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: async (d) => await saveItem(d),
    validationSchema: FormSchema,
  });

  const addNewExpo = React.useCallback(
    (competitionDayIndex: number) => {
      const cday = formik?.values?.competition?.competitionDays[competitionDayIndex];
      cday.expos.push(ExpoDto.fromJS({ name: "Expo " + (+cday.expos.length + 1) }));
      formik?.setFieldValue("competitionDays", [...formik?.values?.competition?.competitionDays]);
    },
    [formik]
  );
  const deleteExpo = React.useCallback(
    (expoIndex, competitionDayIndex) => {
      formik?.values?.competition.competitionDays[competitionDayIndex].expos.splice(expoIndex, 1);
      formik?.setFieldValue(`competition.competitionDays[${competitionDayIndex}].expos`, [
        ...formik?.values?.competition.competitionDays[competitionDayIndex].expos,
      ]);
    },
    [formik]
  );

  const updateDate = React.useCallback(
    (newDate) => {
      formik?.setFieldValue(`competition.competitionDays[${selectedDayDate}].dayDate`, newDate);
      setselectedDayDate(null);
    },
    [setselectedDayDate, selectedDayDate, formik]
  );

  return (
    <SmallFormGenerator
      dirtyCheckSaveButton={false}
      initialValues={data}
      editMode={editMode}
      onSubmit={saveItem}
      loading={loading}
      saving={saving}
      formik={formik}
    >
      {formik?.values?.competition.competitionDays?.map((competitionDay, competitionDayIndex) => (
        <>
          <FieldSet
            title={formatDate(competitionDay.dayDate) ?? ""}
            // rightElement={
            //   <Button icon="edit" minimal intent={"primary"} onClick={() => setselectedDayDate(competitionDayIndex)} />
            // }
          >
            {competitionDay.expos.map((expo, expoIndex) => (
              <FieldGroup columns={[4, 4, 2, 2]}>
                <FGTextInput
                  name={`competition.competitionDays[${competitionDayIndex}].expos[${expoIndex}].name`}
                  label={t(ETLCodes.ExpoName)}
                />

                <FGSelectInput
                  name={`competition.competitionDays[${competitionDayIndex}].expos[${expoIndex}].subActivityId`}
                  label={t(ETLCodes.SubActivity)}
                  items={subActivities}
                  loading={subActivitiesLoading}
                />
                <FGCheckboxInput
                  name={`competition.competitionDays[${competitionDayIndex}].expos[${expoIndex}].cruft`}
                  label={t(ETLCodes.Crufts)}
                />
                <DeleteButton onDelete={() => deleteExpo(expoIndex, competitionDayIndex)} secured={false} />
              </FieldGroup>
            ))}
            <Button
              minimal
              intent={"primary"}
              text={t(ETLCodes.AddExpo)}
              onClick={() => addNewExpo(competitionDayIndex)}
              icon={"plus"}
            />
          </FieldSet>
          <div style={{ height: "1rem" }}></div>
        </>
      ))}

      <Dialog
        title={t(ETLCodes.WarningChangeCompetitionDatesAccordingly)}
        isOpen={selectedDayDate != null}
        onClose={() => setselectedDayDate(null)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 300, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <SmallFormGenerator editMode={true} initialValues={{}} onSubmit={(v: any) => updateDate(v.newDate)}>
            <FGSRSHDateMaskInput name="newDate" label={t(ETLCodes.NewDate)} />
          </SmallFormGenerator>
        </div>
      </Dialog>
    </SmallFormGenerator>
  );
};
